import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  FiPlus, 
  FiEdit2, 
  FiSearch,
  FiX,
  FiCheck,
  FiPackage,
  FiEye,
  FiDollarSign,
  FiHash,
  FiTrash2,
  FiGrid,
  FiList,
  FiAlertCircle,
  FiChevronDown,
  FiChevronUp,
  FiInfo,
  FiFilter,
  FiTrendingUp,
  FiClock,
  FiCalendar,
  FiBox,
  FiCheckCircle,
  FiMinusCircle,
  FiPlusCircle,
  FiLayers,
  FiSave,
  FiRefreshCw,
  FiTag,
  FiArchive,
  FiGift,
  FiLock,
  FiUnlock
} from 'react-icons/fi';
import { basedProductItemAPI, productAPI, accountAPI } from '../utils/api';
import toast from 'react-hot-toast';
import useResponsive from '../hooks/useResponsive';

const BasedProductItems = () => {
  const [items, setItems] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [itemsLoading, setItemsLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [modalType, setModalType] = useState('create');
  const [selectedItem, setSelectedItem] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [limitFilter, setLimitFilter] = useState('all');
  const [sortBy, setSortBy] = useState('newest');
  const [userRole, setUserRole] = useState('');
  const [currentUser, setCurrentUser] = useState(null);
  const [selectedProductId, setSelectedProductId] = useState('');
  const [viewMode, setViewMode] = useState('table');
  const [showProductDropdown, setShowProductDropdown] = useState(false);
  const [productSearch, setProductSearch] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formErrors, setFormErrors] = useState({});

  const [formData, setFormData] = useState({
    product_id: '',
    code: '',
    name: '',
    coin_price: '',
    item_id: '',
    has_limit: false,
    limit_count: ''
  });

  const { isMobile, isTablet, isDesktop } = useResponsive();

  // Color theme based on sidebar based-items (purple)
  const theme = {
    primary: 'from-purple-600 to-purple-700',
    primaryLight: 'from-purple-500 to-purple-600',
    secondary: 'from-purple-500 to-pink-500',
    bg: 'bg-purple-100 dark:bg-purple-900/30',
    text: 'text-purple-600 dark:text-purple-400',
    border: 'border-purple-200 dark:border-purple-800',
    ring: 'ring-purple-500',
    hover: 'hover:bg-purple-50 dark:hover:bg-purple-900/20',
    gradient: 'from-purple-600 to-pink-600',
    lightBg: 'bg-purple-50 dark:bg-purple-900/20',
    badge: {
      success: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
      warning: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400',
      error: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
      info: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400'
    }
  };

  useEffect(() => {
    fetchUserAndData();
  }, []);

  useEffect(() => {
    if (selectedProductId) {
      fetchItems(selectedProductId);
    } else {
      setItems([]);
    }
  }, [selectedProductId]);

  const fetchUserAndData = async () => {
    try {
      setLoading(true);
      const userResponse = await accountAPI.getMe();
      if (userResponse.data.success) {
        const user = userResponse.data.data;
        setCurrentUser(user);
        setUserRole(user.role);
      }

      const productsResponse = await productAPI.list();
      if (productsResponse.data && productsResponse.data.success) {
        let allProducts = productsResponse.data.data || [];
        
        if (userRole === 'super_admin' && currentUser) {
          allProducts = allProducts.filter(p => p.admin_id === currentUser?.id);
        }
        
        setProducts(allProducts);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to fetch products');
    } finally {
      setLoading(false);
    }
  };

  const fetchItems = async (productId) => {
    if (!productId) return;
    
    setItemsLoading(true);
    try {
      const response = await basedProductItemAPI.list(productId);
      if (response.data && response.data.success) {
        setItems(response.data.data || []);
      } else {
        setItems([]);
      }
    } catch (error) {
      console.error('Error fetching items:', error);
      setItems([]);
    } finally {
      setItemsLoading(false);
    }
  };

  const canCreate = () => userRole === 'system';
  const canEdit = () => userRole === 'system';
  const canDelete = () => userRole === 'system';
  const canView = () => ['system', 'super_admin'].includes(userRole);

  const canAddItemsToProduct = (productId) => {
    const product = products.find(p => p.id === parseInt(productId));
    return product?.is_stock_based === 0;
  };

  const getProductDetails = (productId) => {
    return products.find(p => p.id === parseInt(productId));
  };

  const normalizeMoney = (val) => {
    if (val === null || val === undefined) return '0';
    const s = String(val).trim();
    return s === '' ? '0' : s;
  };

  const toFloat = (val) => {
    return parseFloat(normalizeMoney(val)) || 0;
  };

  const formatCoin = (val) => {
    const num = toFloat(val);
    return num.toFixed(2);
  };

  const handleCodeChange = (e) => {
    const value = e.target.value;
    const allowedValue = value.toLowerCase().replace(/[^a-z0-9_-\s]/g, '');
    setFormData({ ...formData, code: allowedValue });
    if (formErrors.code) {
      setFormErrors({ ...formErrors, code: null });
    }
  };

  const validateForm = () => {
    const errors = {};

    if (!formData.code) {
      errors.code = 'Code is required';
    } else if (formData.code.length < 2) {
      errors.code = 'Code must be at least 2 characters';
    }

    if (!formData.name) {
      errors.name = 'Name is required';
    } else if (formData.name.length < 3) {
      errors.name = 'Name must be at least 3 characters';
    }

    if (!formData.item_id) {
      errors.item_id = 'Item ID is required';
    } else if (parseInt(formData.item_id) <= 0) {
      errors.item_id = 'Item ID must be greater than 0';
    }

    if (formData.coin_price && isNaN(parseFloat(formData.coin_price))) {
      errors.coin_price = 'Coin price must be a valid number';
    }

    if (!formData.product_id) {
      errors.product_id = 'Please select a product';
    } else if (!canAddItemsToProduct(formData.product_id)) {
      errors.product_id = 'Cannot add items to stock-based products';
    }

    // Validate limit fields
    if (formData.has_limit) {
      if (!formData.limit_count || formData.limit_count === '') {
        errors.limit_count = 'Limit count is required when has_limit is true';
      } else {
        const limitCount = parseInt(formData.limit_count);
        if (isNaN(limitCount) || limitCount <= 0) {
          errors.limit_count = 'Limit count must be greater than 0';
        }
      }
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleCreate = async () => {
    if (!canCreate()) {
      toast.error('You do not have permission to create items');
      return;
    }

    if (!validateForm()) {
      toast.error('Please fix the errors in the form');
      return;
    }

    setIsSubmitting(true);

    try {
      const createData = {
        product_id: parseInt(formData.product_id),
        code: formData.code,
        name: formData.name,
        item_id: parseInt(formData.item_id),
        has_limit: formData.has_limit ? 1 : 0
      };

      if (formData.coin_price) {
        createData.coin_price = parseFloat(formData.coin_price);
      }

      if (formData.has_limit && formData.limit_count) {
        createData.limit_count = parseInt(formData.limit_count);
      }

      const response = await basedProductItemAPI.create(createData);
      
      if (response.data && response.data.success) {
        toast.success('Item created successfully');
        setShowModal(false);
        resetForm();
        fetchItems(selectedProductId);
      } else {
        toast.error(response.data?.message || 'Failed to create item');
      }
    } catch (error) {
      console.error('Error creating item:', error);
      toast.error(error.response?.data?.message || 'Failed to create item');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUpdate = async () => {
    if (!canEdit()) {
      toast.error('You do not have permission to update items');
      return;
    }

    if (!selectedItem) {
      toast.error('No item selected');
      return;
    }

    if (!validateForm()) {
      toast.error('Please fix the errors in the form');
      return;
    }

    setIsSubmitting(true);

    const updateData = { based_item_id: selectedItem.id };

    if (formData.code && formData.code !== selectedItem.code) {
      updateData.code = formData.code;
    }
    
    if (formData.name && formData.name !== selectedItem.name) {
      updateData.name = formData.name;
    }
    
    if (formData.coin_price && parseFloat(formData.coin_price) !== selectedItem.coin_price) {
      updateData.coin_price = parseFloat(formData.coin_price);
    }

    if (formData.item_id && parseInt(formData.item_id) !== selectedItem.item_id) {
      updateData.item_id = parseInt(formData.item_id);
    }

    // Handle limit fields
    const hasLimitValue = formData.has_limit ? 1 : 0;
    if (hasLimitValue !== selectedItem.has_limit) {
      updateData.has_limit = hasLimitValue;
    }

    if (formData.has_limit && formData.limit_count) {
      const limitCount = parseInt(formData.limit_count);
      if (limitCount !== selectedItem.limit_count) {
        updateData.limit_count = limitCount;
      }
    } else if (!formData.has_limit && selectedItem.has_limit === 1) {
      // If changing from has_limit true to false, set limit_count to null
      updateData.has_limit = 0;
      updateData.limit_count = null;
    }

    if (Object.keys(updateData).length === 1) {
      toast.error('No changes to update');
      setIsSubmitting(false);
      return;
    }

    try {
      const result = await basedProductItemAPI.update(updateData);
      
      if (result && result.success === true) {
        toast.success('Item updated successfully');
        setShowModal(false);
        resetForm();
        fetchItems(selectedProductId);
      } else {
        toast.error(result?.message || 'Failed to update item');
      }
    } catch (error) {
      console.error('Error updating item:', error);
      toast.error(error.response?.data?.message || 'Failed to update item');
    } finally {
      setIsSubmitting(false);
    }
  };

  const confirmDelete = (item) => {
    setItemToDelete(item);
    setShowDeleteModal(true);
  };

  const handleDelete = async () => {
    if (!canDelete() || !itemToDelete) return;

    try {
      const response = await basedProductItemAPI.delete(itemToDelete.id);
      if (response.data && response.data.success) {
        toast.success('Item deleted successfully');
        fetchItems(selectedProductId);
      } else {
        toast.error(response.data?.message || 'Failed to delete item');
      }
    } catch (error) {
      console.error('Error deleting item:', error);
      toast.error(error.response?.data?.message || 'Failed to delete item');
    } finally {
      setShowDeleteModal(false);
      setItemToDelete(null);
    }
  };

  const openEditModal = (item) => {
    if (!canEdit()) {
      toast.error('You do not have permission to edit items');
      return;
    }

    setSelectedItem(item);
    setFormData({
      product_id: item.product_id,
      code: item.code,
      name: item.name,
      coin_price: item.coin_price || '',
      item_id: item.item_id || '',
      has_limit: item.has_limit === 1,
      limit_count: item.limit_count || ''
    });
    setModalType('edit');
    setShowModal(true);
    setFormErrors({});
  };

  const openCreateModal = () => {
    if (!canCreate()) {
      toast.error('You do not have permission to create items');
      return;
    }

    if (!selectedProductId) {
      toast.error('Please select a product first');
      return;
    }

    if (!canAddItemsToProduct(selectedProductId)) {
      toast.error('Cannot add items to stock-based products');
      return;
    }

    resetForm();
    setFormData(prev => ({ 
      ...prev, 
      product_id: selectedProductId,
      has_limit: false,
      limit_count: ''
    }));
    setModalType('create');
    setShowModal(true);
    setFormErrors({});
  };

  const resetForm = () => {
    setFormData({
      product_id: selectedProductId || '',
      code: '',
      name: '',
      coin_price: '',
      item_id: '',
      has_limit: false,
      limit_count: ''
    });
    setSelectedItem(null);
    setFormErrors({});
  };

  const getSortedItems = () => {
    let filtered = items.filter(item => {
      const matchesSearch = item.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           item.code?.toLowerCase().includes(searchTerm.toLowerCase());
      
      const matchesStatus = statusFilter === 'all' || 
                           (statusFilter === 'hasCoin' && parseFloat(item.coin_price || 0) > 0) ||
                           (statusFilter === 'noCoin' && parseFloat(item.coin_price || 0) === 0);
      
      const matchesLimit = limitFilter === 'all' ||
                          (limitFilter === 'hasLimit' && item.has_limit === 1) ||
                          (limitFilter === 'noLimit' && item.has_limit === 0);
      
      return matchesSearch && matchesStatus && matchesLimit;
    });

    switch(sortBy) {
      case 'newest':
        return filtered.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
      case 'oldest':
        return filtered.sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
      case 'name':
        return filtered.sort((a, b) => a.name.localeCompare(b.name));
      case 'code':
        return filtered.sort((a, b) => a.code.localeCompare(b.code));
      case 'price-high':
        return filtered.sort((a, b) => (parseFloat(b.coin_price) || 0) - (parseFloat(a.coin_price) || 0));
      case 'price-low':
        return filtered.sort((a, b) => (parseFloat(a.coin_price) || 0) - (parseFloat(b.coin_price) || 0));
      default:
        return filtered;
    }
  };

  const displayedItems = getSortedItems();

  const totalItems = displayedItems.length;
  const itemsWithCoin = displayedItems.filter(i => parseFloat(i.coin_price || 0) > 0).length;
  const itemsWithLimit = displayedItems.filter(i => i.has_limit === 1).length;
  const totalCoinValue = displayedItems.reduce((sum, item) => sum + (parseFloat(item.coin_price) || 0), 0);
  const averageCoinValue = totalItems > 0 ? (totalCoinValue / totalItems).toFixed(2) : '0.00';

  const filteredProducts = products.filter(product =>
    product.is_stock_based === 0 && // Only show non-stock products
    (product.name.toLowerCase().includes(productSearch.toLowerCase()) ||
    product.code.toLowerCase().includes(productSearch.toLowerCase()))
  );

  const isProductNonStock = (productId) => {
    const product = products.find(p => p.id === parseInt(productId));
    return product?.is_stock_based === 0;
  };

  const selectedProduct = getProductDetails(selectedProductId);

  const ItemGridCard = ({ item }) => {
    const canEditThis = canEdit();
    const hasCoin = parseFloat(item.coin_price || 0) > 0;
    const hasLimit = item.has_limit === 1;
    
    return (
      <motion.div
        layout
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.9 }}
        whileHover={{ y: -4 }}
        className="group bg-white dark:bg-gray-800 rounded-2xl p-5 shadow-sm hover:shadow-xl transition-all duration-300 border border-gray-100 dark:border-gray-700"
      >
        <div className="flex flex-col h-full">
          <div className="flex items-center justify-between mb-4">
            <div className={`w-10 h-10 rounded-xl ${theme.bg} flex items-center justify-center`}>
              <FiPackage className={`w-5 h-5 ${theme.text}`} />
            </div>
            <span className="text-xs text-gray-400">ID: {item.id}</span>
          </div>

          <h3 className="font-semibold text-gray-900 dark:text-white text-lg mb-1 line-clamp-1">
            {item.name}
          </h3>
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-3 flex items-center gap-1">
            <FiHash className="w-3 h-3" />
            {item.code}
          </p>

          <div className="flex flex-wrap gap-2 mt-auto">
            <span className={`px-3 py-1.5 rounded-lg text-xs font-medium flex items-center gap-1 ${
              hasCoin 
                ? 'bg-purple-50 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300' 
                : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400'
            }`}>
              <FiDollarSign className="w-3 h-3" />
              {formatCoin(item.coin_price)} Coin
            </span>
            
            <span className="px-3 py-1.5 bg-indigo-50 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 rounded-lg text-xs font-medium">
              <FiHash className="inline w-3 h-3 mr-1" />
              {item.item_id}
            </span>

            {hasLimit && (
              <span className="px-3 py-1.5 bg-amber-50 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 rounded-lg text-xs font-medium flex items-center gap-1">
                <FiLock className="w-3 h-3" />
                Limit: {item.limit_count}
              </span>
            )}
          </div>

          <div className="mt-4 pt-4 border-t border-gray-100 dark:border-gray-700 flex justify-between items-center">
            <div className="flex items-center gap-1 text-xs text-gray-400">
              <FiCalendar className="w-3 h-3" />
              {new Date(item.created_at).toLocaleDateString()}
            </div>
            {canEditThis && (
              <div className="flex gap-1">
                <motion.button
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                  onClick={() => openEditModal(item)}
                  className={`p-2 ${theme.hover} rounded-xl transition-colors ${theme.text}`}
                  title="Edit item"
                >
                  <FiEdit2 className="w-4 h-4" />
                </motion.button>
                <motion.button
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                  onClick={() => confirmDelete(item)}
                  className="p-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-xl transition-colors"
                  title="Delete item"
                >
                  <FiTrash2 className="w-4 h-4" />
                </motion.button>
              </div>
            )}
          </div>
        </div>
      </motion.div>
    );
  };

  const ItemTableRow = ({ item }) => {
    const canEditThis = canEdit();
    const hasCoin = parseFloat(item.coin_price || 0) > 0;
    const hasLimit = item.has_limit === 1;
    
    return (
      <motion.tr
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
      >
        <td className="px-4 sm:px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-200">
          {item.id}
        </td>
        <td className="px-4 sm:px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-200">
          {item.code}
        </td>
        <td className="px-4 sm:px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-200">
          {item.name}
        </td>
        <td className="px-4 sm:px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-200">
          {item.item_id}
        </td>
        <td className="px-4 sm:px-6 py-4 whitespace-nowrap">
          <span className={`px-2 py-1 rounded-lg text-xs font-medium ${
            hasCoin ? theme.badge.info : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400'
          }`}>
            {formatCoin(item.coin_price)} Coin
          </span>
        </td>
        <td className="px-4 sm:px-6 py-4 whitespace-nowrap">
          {hasLimit ? (
            <span className="px-2 py-1 bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-400 rounded-lg text-xs font-medium flex items-center gap-1 w-fit">
              <FiLock className="w-3 h-3" />
              Limit: {item.limit_count}
            </span>
          ) : (
            <span className="px-2 py-1 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 rounded-lg text-xs font-medium flex items-center gap-1 w-fit">
              <FiUnlock className="w-3 h-3" />
              No Limit
            </span>
          )}
        </td>
        <td className="px-4 sm:px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
          <div className="flex items-center gap-1">
            <FiCalendar className="w-3 h-3" />
            {new Date(item.created_at).toLocaleDateString()}
          </div>
        </td>
        <td className="px-4 sm:px-6 py-4 whitespace-nowrap text-sm">
          {canEditThis && (
            <div className="flex items-center gap-2">
              <motion.button
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                onClick={() => openEditModal(item)}
                className={`p-2 ${theme.hover} rounded-lg transition-colors ${theme.text}`}
                title="Edit"
              >
                <FiEdit2 className="w-4 h-4" />
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                onClick={() => confirmDelete(item)}
                className="p-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-lg transition-colors"
                title="Delete"
              >
                <FiTrash2 className="w-4 h-4" />
              </motion.button>
            </div>
          )}
          {!canEditThis && canView() && (
            <span className="text-gray-400 text-xs flex items-center">
              <FiEye className="mr-1" /> View
            </span>
          )}
        </td>
      </motion.tr>
    );
  };

  if (!canView()) {
    return (
      <div className="min-h-[400px] flex items-center justify-center">
        <div className="text-center">
          <div className="w-20 h-20 mx-auto bg-red-100 dark:bg-red-900/20 rounded-full flex items-center justify-center mb-4">
            <FiEye className="w-10 h-10 text-red-500 dark:text-red-400" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Access Denied</h3>
          <p className="text-gray-500 dark:text-gray-400">You do not have permission to view this page</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 pb-20 md:pb-6">
      {/* Header Section */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className={`bg-gradient-to-r ${theme.gradient} rounded-2xl p-6 sm:p-8 shadow-lg`}
      >
        <div className="flex items-center gap-4">
          <motion.div
            initial={{ scale: 0, rotate: -180 }}
            animate={{ scale: 1, rotate: 0 }}
            transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
            className="w-14 h-14 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center"
          >
            <FiLayers className="w-7 h-7 text-white" />
          </motion.div>
          <div>
            <motion.h1
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.1 }}
              className="text-2xl md:text-3xl font-bold text-white"
            >
              Based Product Items
            </motion.h1>
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.2 }}
              className="text-purple-100 text-sm mt-1"
            >
              Manage items for non-stock based products with usage limits
            </motion.p>
          </div>
        </div>
      </motion.div>

      {/* Product Selection Card */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm border border-gray-100 dark:border-gray-700"
      >
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
          Select Product <span className="text-red-500">*</span>
        </label>
        
        <div className="relative">
          <button
            onClick={() => setShowProductDropdown(!showProductDropdown)}
            className="w-full px-4 py-3 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent text-left flex items-center justify-between text-gray-900 dark:text-white hover:border-purple-300 transition-colors"
          >
            <span className="truncate flex items-center gap-2">
              {selectedProduct ? (
                <>
                  <FiPackage className={`w-4 h-4 ${theme.text}`} />
                  <span className="font-medium">{selectedProduct.name}</span>
                  <span className="text-gray-400">({selectedProduct.code})</span>
                </>
              ) : (
                <span className="text-gray-400">-- Choose a product --</span>
              )}
            </span>
            <motion.div
              animate={{ rotate: showProductDropdown ? 180 : 0 }}
              transition={{ duration: 0.2 }}
            >
              <FiChevronDown className="w-5 h-5 text-gray-400" />
            </motion.div>
          </button>

          <AnimatePresence>
            {showProductDropdown && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                className="absolute z-10 mt-2 w-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-lg overflow-hidden"
              >
                <div className="p-3 border-b border-gray-100 dark:border-gray-700">
                  <div className="relative">
                    <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                    <input
                      type="text"
                      placeholder="Search products..."
                      value={productSearch}
                      onChange={(e) => setProductSearch(e.target.value)}
                      className="w-full pl-9 pr-4 py-2 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm text-gray-900 dark:text-white placeholder-gray-400"
                      onClick={(e) => e.stopPropagation()}
                    />
                  </div>
                </div>

                <div className="max-h-60 overflow-y-auto scrollbar-hide">
                  {filteredProducts.length === 0 ? (
                    <div className="p-4 text-center text-sm text-gray-500 dark:text-gray-400">
                      No products found
                    </div>
                  ) : (
                    filteredProducts.map((product) => {
                      const isStockBased = product.is_stock_based === 1;
                      return (
                        <motion.button
                          key={product.id}
                          whileHover={{ backgroundColor: 'rgba(139, 92, 246, 0.05)' }}
                          onClick={() => {
                            setSelectedProductId(String(product.id));
                            setShowProductDropdown(false);
                            setProductSearch('');
                          }}
                          className="w-full px-4 py-3 text-left flex items-center justify-between border-b border-gray-100 dark:border-gray-700 last:border-0"
                        >
                          <div className="flex items-center gap-3 min-w-0">
                            <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                              isStockBased
                                ? 'bg-amber-100 dark:bg-amber-900/30'
                                : 'bg-purple-100 dark:bg-purple-900/30'
                            }`}>
                              <FiPackage className={`w-4 h-4 ${
                                isStockBased
                                  ? 'text-amber-600 dark:text-amber-400'
                                  : 'text-purple-600 dark:text-purple-400'
                              }`} />
                            </div>
                            <div className="min-w-0">
                              <p className="font-medium text-sm text-gray-900 dark:text-white truncate">
                                {product.name}
                              </p>
                              <p className="text-xs text-gray-500 dark:text-gray-400">
                                Code: {product.code}
                              </p>
                            </div>
                          </div>
                          {selectedProductId === String(product.id) && (
                            <FiCheckCircle className={`w-5 h-5 ${theme.text}`} />
                          )}
                        </motion.button>
                      );
                    })
                  )}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {selectedProductId && selectedProduct && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mt-4"
          >
          </motion.div>
        )}
      </motion.div>

      {selectedProductId && (
        <>
          {/* Statistics Cards */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="grid grid-cols-2 lg:grid-cols-4 gap-4"
          >
            {[
              { label: 'Total Items', value: totalItems, icon: FiPackage, color: 'purple' },
              { label: 'With Coin', value: itemsWithCoin, icon: FiDollarSign, color: 'green' },
              { label: 'With Limit', value: itemsWithLimit, icon: FiLock, color: 'amber' },
              { label: 'Avg Coin', value: `${averageCoinValue} Coin`, icon: FiTrendingUp, color: 'pink' }
            ].map((stat, index) => (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.2 + index * 0.1 }}
                className="bg-white dark:bg-gray-800 rounded-xl p-5 shadow-sm border border-gray-100 dark:border-gray-700 hover:shadow-md transition-all"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500 dark:text-gray-400">{stat.label}</p>
                    <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">{stat.value}</p>
                  </div>
                  <div className={`w-10 h-10 bg-${stat.color}-100 dark:bg-${stat.color}-900/30 rounded-lg flex items-center justify-center`}>
                    <stat.icon className={`w-5 h-5 text-${stat.color}-600 dark:text-${stat.color}-400`} />
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>

          {/* Search and Actions Bar */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
            className="flex flex-col gap-4"
          >
            <div className="flex flex-col sm:flex-row gap-3">
              <div className="relative flex-1">
                <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type="text"
                  placeholder="Search by name or code..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent text-gray-900 dark:text-white placeholder-gray-400"
                />
              </div>

              <div className="flex gap-2">
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="px-4 py-3 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm text-gray-900 dark:text-white"
                >
                  <option value="newest">Newest</option>
                  <option value="oldest">Oldest</option>
                  <option value="name">Name</option>
                  <option value="code">Code</option>
                  <option value="price-high">Price (High to Low)</option>
                  <option value="price-low">Price (Low to High)</option>
                </select>

                <button
                  onClick={() => setShowFilters(!showFilters)}
                  className={`px-4 py-3 border rounded-xl flex items-center gap-2 transition-colors ${
                    showFilters 
                      ? 'bg-purple-600 text-white border-purple-600' 
                      : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:border-purple-300'
                  }`}
                >
                  <FiFilter className="w-5 h-5" />
                  <span className="hidden sm:inline">Filter</span>
                </button>

                {!isMobile && (
                  <div className="hidden sm:flex bg-gray-100 dark:bg-gray-700 rounded-lg p-1">
                    <button
                      onClick={() => setViewMode('table')}
                      className={`p-2 rounded-lg transition-colors ${
                        viewMode === 'table' 
                          ? 'bg-white dark:bg-gray-600 text-purple-600 shadow-sm' 
                          : 'text-gray-500 hover:text-gray-700 dark:text-gray-400'
                      }`}
                      title="Table view"
                    >
                      <FiList className="w-5 h-5" />
                    </button>
                    <button
                      onClick={() => setViewMode('grid')}
                      className={`p-2 rounded-lg transition-colors ${
                        viewMode === 'grid' 
                          ? 'bg-white dark:bg-gray-600 text-purple-600 shadow-sm' 
                          : 'text-gray-500 hover:text-gray-700 dark:text-gray-400'
                      }`}
                      title="Grid view"
                    >
                      <FiGrid className="w-5 h-5" />
                    </button>
                  </div>
                )}

                {canCreate() && canAddItemsToProduct(selectedProductId) && (
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={openCreateModal}
                    className={`px-4 py-3 bg-gradient-to-r ${theme.primary} text-white rounded-xl hover:from-purple-700 hover:to-purple-800 transition-all shadow-lg shadow-purple-500/20 flex items-center gap-2 whitespace-nowrap`}
                  >
                    <FiPlus className="w-5 h-5" />
                    <span className="hidden sm:inline">Add New Item</span>
                  </motion.button>
                )}
              </div>
            </div>

            <AnimatePresence>
              {showFilters && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  className="bg-white dark:bg-gray-800 rounded-xl p-4 shadow-sm border border-gray-100 dark:border-gray-700"
                >
                  <div className="flex flex-wrap items-center gap-4">
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Coin Status:</span>
                    <div className="flex gap-2">
                      {[
                        { value: 'all', label: 'All' },
                        { value: 'hasCoin', label: 'Has Coin' },
                        { value: 'noCoin', label: 'No Coin' }
                      ].map((status) => (
                        <motion.button
                          key={status.value}
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                          onClick={() => setStatusFilter(status.value)}
                          className={`px-3 py-1.5 rounded-lg text-sm transition-colors ${
                            statusFilter === status.value
                              ? status.value === 'hasCoin' 
                                ? 'bg-purple-600 text-white'
                                : status.value === 'noCoin'
                                ? 'bg-gray-600 text-white'
                                : 'bg-purple-600 text-white'
                              : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                          }`}
                        >
                          {status.label}
                        </motion.button>
                      ))}
                    </div>

                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300 ml-4">Limit Status:</span>
                    <div className="flex gap-2">
                      {[
                        { value: 'all', label: 'All' },
                        { value: 'hasLimit', label: 'Has Limit' },
                        { value: 'noLimit', label: 'No Limit' }
                      ].map((status) => (
                        <motion.button
                          key={status.value}
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                          onClick={() => setLimitFilter(status.value)}
                          className={`px-3 py-1.5 rounded-lg text-sm transition-colors ${
                            limitFilter === status.value
                              ? status.value === 'hasLimit' 
                                ? 'bg-amber-600 text-white'
                                : status.value === 'noLimit'
                                ? 'bg-gray-600 text-white'
                                : 'bg-amber-600 text-white'
                              : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                          }`}
                        >
                          {status.label}
                        </motion.button>
                      ))}
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>

          {/* Items Display */}
          {itemsLoading ? (
            <div className="flex justify-center items-center h-64">
              <div className="relative">
                <div className="w-16 h-16 border-4 border-purple-200 dark:border-purple-900 rounded-full"></div>
                <div className="w-16 h-16 border-4 border-purple-600 border-t-transparent rounded-full animate-spin absolute top-0 left-0"></div>
              </div>
            </div>
          ) : displayedItems.length === 0 ? (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white dark:bg-gray-800 rounded-2xl p-12 text-center border border-gray-100 dark:border-gray-700"
            >
              <div className="w-24 h-24 mx-auto bg-gradient-to-br from-purple-100 to-pink-100 dark:from-purple-900/30 dark:to-pink-900/30 rounded-full flex items-center justify-center mb-4">
                <FiPackage className="w-12 h-12 text-purple-600 dark:text-purple-400" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">No Items Found</h3>
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
                {searchTerm || statusFilter !== 'all' || limitFilter !== 'all' || sortBy !== 'newest'
                  ? 'No items match your filters.' 
                  : canAddItemsToProduct(selectedProductId)
                    ? 'Get started by adding your first item.'
                    : 'This is a stock-based product. Items cannot be added.'}
              </p>
              {canCreate() && canAddItemsToProduct(selectedProductId) && !searchTerm && statusFilter === 'all' && limitFilter === 'all' && sortBy === 'newest' && (
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={openCreateModal}
                  className={`inline-flex items-center px-4 py-2 bg-gradient-to-r ${theme.primary} text-white rounded-lg hover:from-purple-700 hover:to-purple-800 transition-colors text-sm shadow-lg shadow-purple-500/20`}
                >
                  <FiPlus className="mr-2 w-4 h-4" />
                  Add your first item
                </motion.button>
              )}
            </motion.div>
          ) : (
            <>
              {isMobile ? (
                <div className="grid grid-cols-1 gap-4">
                  <AnimatePresence>
                    {displayedItems.map((item) => (
                      <ItemGridCard key={item.id} item={item} />
                    ))}
                  </AnimatePresence>
                </div>
              ) : (
                <>
                  {viewMode === 'grid' ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      <AnimatePresence>
                        {displayedItems.map((item) => (
                          <ItemGridCard key={item.id} item={item} />
                        ))}
                      </AnimatePresence>
                    </div>
                  ) : (
                    <div className="bg-white dark:bg-gray-800 rounded-xl overflow-hidden shadow-sm border border-gray-100 dark:border-gray-700">
                      <div className="overflow-x-auto scrollbar-hide">
                        <table className="w-full min-w-[900px]">
                          <thead className="bg-gray-50 dark:bg-gray-700">
                            <tr>
                              <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">ID</th>
                              <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Code</th>
                              <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Name</th>
                              <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Item ID</th>
                              <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Coin Price</th>
                              <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Limit</th>
                              <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Created</th>
                              <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Actions</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                            <AnimatePresence>
                              {displayedItems.map((item) => (
                                <ItemTableRow key={item.id} item={item} />
                              ))}
                            </AnimatePresence>
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </>
              )}
            </>
          )}
        </>
      )}

      {/* Create/Edit Modal */}
      <AnimatePresence>
        {showModal && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setShowModal(false)}
              className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40"
            />
            
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              className="fixed inset-0 z-50 flex items-center justify-center p-4"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-hidden flex flex-col">
                <div className={`bg-gradient-to-r ${theme.gradient} px-6 py-5`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <motion.div
                        initial={{ scale: 0, rotate: -180 }}
                        animate={{ scale: 1, rotate: 0 }}
                        transition={{ type: "spring", stiffness: 200 }}
                        className="w-10 h-10 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center"
                      >
                        {modalType === 'create' ? (
                          <FiPlus className="w-5 h-5 text-white" />
                        ) : (
                          <FiEdit2 className="w-5 h-5 text-white" />
                        )}
                      </motion.div>
                      <div>
                        <h3 className="text-lg font-semibold text-white">
                          {modalType === 'create' ? 'Create New Item' : 'Edit Item'}
                        </h3>
                        <p className="text-sm text-purple-100">
                          Product: {selectedProduct?.name}
                        </p>
                      </div>
                    </div>
                    <motion.button
                      whileHover={{ scale: 1.1, rotate: 90 }}
                      whileTap={{ scale: 0.9 }}
                      onClick={() => setShowModal(false)}
                      className="p-2 hover:bg-white/20 rounded-xl transition-colors"
                    >
                      <FiX className="w-5 h-5 text-white" />
                    </motion.button>
                  </div>
                </div>

                <div className="flex-1 overflow-y-auto scrollbar-hide p-6 bg-gray-50 dark:bg-gray-900">
                  <div className="space-y-5">
                    {/* Code Field */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Code <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={formData.code}
                        onChange={handleCodeChange}
                        className={`
                          w-full px-4 py-3 bg-white dark:bg-gray-800 
                          border-2 rounded-xl text-sm text-gray-900 dark:text-white
                          focus:ring-2 focus:ring-purple-500 focus:border-transparent
                          transition-all duration-200
                          ${formErrors.code 
                            ? 'border-red-300 focus:ring-red-500' 
                            : 'border-gray-200 dark:border-gray-700 hover:border-purple-300'
                          }
                        `}
                        placeholder="e.g., item_01"
                      />
                      {formErrors.code && (
                        <motion.p
                          initial={{ opacity: 0, y: -10 }}
                          animate={{ opacity: 1, y: 0 }}
                          className="mt-2 text-xs text-red-500 flex items-center gap-1"
                        >
                          <FiAlertCircle className="w-3 h-3" />
                          {formErrors.code}
                        </motion.p>
                      )}
                      <p className="mt-2 text-xs text-gray-500">
                        Lowercase letters, numbers, hyphens, underscores only
                      </p>
                    </div>

                    {/* Name Field */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Name <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        className={`
                          w-full px-4 py-3 bg-white dark:bg-gray-800 
                          border-2 rounded-xl text-sm text-gray-900 dark:text-white
                          focus:ring-2 focus:ring-purple-500 focus:border-transparent
                          transition-all duration-200
                          ${formErrors.name 
                            ? 'border-red-300 focus:ring-red-500' 
                            : 'border-gray-200 dark:border-gray-700 hover:border-purple-300'
                          }
                        `}
                        placeholder="Enter item name"
                      />
                      {formErrors.name && (
                        <motion.p
                          initial={{ opacity: 0, y: -10 }}
                          animate={{ opacity: 1, y: 0 }}
                          className="mt-2 text-xs text-red-500 flex items-center gap-1"
                        >
                          <FiAlertCircle className="w-3 h-3" />
                          {formErrors.name}
                        </motion.p>
                      )}
                    </div>

                    {/* Item ID Field */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Item ID <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="number"
                        min="1"
                        value={formData.item_id}
                        onChange={(e) => setFormData({ ...formData, item_id: e.target.value })}
                        className={`
                          w-full px-4 py-3 bg-white dark:bg-gray-800 
                          border-2 rounded-xl text-sm text-gray-900 dark:text-white
                          focus:ring-2 focus:ring-purple-500 focus:border-transparent
                          transition-all duration-200
                          ${formErrors.item_id 
                            ? 'border-red-300 focus:ring-red-500' 
                            : 'border-gray-200 dark:border-gray-700 hover:border-purple-300'
                          }
                        `}
                        placeholder="Enter item ID"
                      />
                      {formErrors.item_id && (
                        <motion.p
                          initial={{ opacity: 0, y: -10 }}
                          animate={{ opacity: 1, y: 0 }}
                          className="mt-2 text-xs text-red-500 flex items-center gap-1"
                        >
                          <FiAlertCircle className="w-3 h-3" />
                          {formErrors.item_id}
                        </motion.p>
                      )}
                    </div>

                    {/* Coin Price Field */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Coin Price
                      </label>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        value={formData.coin_price}
                        onChange={(e) => setFormData({ ...formData, coin_price: e.target.value })}
                        className={`
                          w-full px-4 py-3 bg-white dark:bg-gray-800 
                          border-2 rounded-xl text-sm text-gray-900 dark:text-white
                          focus:ring-2 focus:ring-purple-500 focus:border-transparent
                          transition-all duration-200
                          ${formErrors.coin_price 
                            ? 'border-red-300 focus:ring-red-500' 
                            : 'border-gray-200 dark:border-gray-700 hover:border-purple-300'
                          }
                        `}
                        placeholder="0.00"
                      />
                      {formErrors.coin_price && (
                        <motion.p
                          initial={{ opacity: 0, y: -10 }}
                          animate={{ opacity: 1, y: 0 }}
                          className="mt-2 text-xs text-red-500 flex items-center gap-1"
                        >
                          <FiAlertCircle className="w-3 h-3" />
                          {formErrors.coin_price}
                        </motion.p>
                      )}
                    </div>

                    {/* Has Limit Toggle */}
                    <div className="pt-4 border-t-2 border-gray-100 dark:border-gray-700">
                      <div className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-4 sm:p-5">
                        <div className="flex flex-col gap-4">
                          <div className="min-w-0">
                            <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
                              Has Limit
                            </p>
                            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400 leading-5">
                              Enable if this item should have a usage limit
                            </p>
                          </div>

                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            {[
                              {
                                value: false,
                                label: 'No Limit',
                                helper: 'Reusable without count restriction',
                                icon: FiUnlock,
                                activeClasses: 'border-teal-300 bg-teal-50 text-teal-700 shadow-md shadow-teal-500/10 dark:border-teal-800 dark:bg-teal-900/20 dark:text-teal-300'
                              },
                              {
                                value: true,
                                label: 'Has Limit',
                                helper: 'Requires a maximum usage count',
                                icon: FiLock,
                                activeClasses: 'border-purple-300 bg-purple-50 text-purple-700 shadow-md shadow-purple-500/10 dark:border-purple-800 dark:bg-purple-900/20 dark:text-purple-300'
                              }
                            ].map((option) => {
                              const isActive = formData.has_limit === option.value;

                              return (
                                <button
                                  key={option.label}
                                  type="button"
                                  onClick={() => {
                                    setFormData({
                                      ...formData,
                                      has_limit: option.value,
                                      limit_count: option.value ? formData.limit_count : ''
                                    });
                                    if (formErrors.limit_count) {
                                      setFormErrors({ ...formErrors, limit_count: null });
                                    }
                                  }}
                                  className={`w-full rounded-2xl border px-4 py-4 text-left transition-all focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 dark:focus:ring-offset-gray-900 ${
                                    isActive
                                      ? option.activeClasses
                                      : 'border-gray-200 bg-gray-50 text-gray-500 hover:border-purple-200 hover:bg-purple-50/40 dark:border-gray-700 dark:bg-gray-900/40 dark:text-gray-400 dark:hover:border-purple-800 dark:hover:bg-purple-900/10'
                                  }`}
                                >
                                  <div className="flex items-start gap-3">
                                    <div className={`mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ${
                                      isActive
                                        ? option.value
                                          ? 'bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300'
                                          : 'bg-teal-100 text-teal-700 dark:bg-teal-900/40 dark:text-teal-300'
                                        : 'bg-white text-gray-400 dark:bg-gray-800 dark:text-gray-500'
                                    }`}>
                                      <option.icon className="h-4 w-4" />
                                    </div>
                                    <div className="min-w-0">
                                      <p className="text-sm font-semibold">
                                        {option.label}
                                      </p>
                                      <p className="mt-1 text-xs leading-5 opacity-80">
                                        {option.helper}
                                      </p>
                                    </div>
                                  </div>
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Limit Count Field (shown only if has_limit is true) */}
                    <AnimatePresence>
                      {formData.has_limit && (
                        <motion.div
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: 'auto' }}
                          exit={{ opacity: 0, height: 0 }}
                          transition={{ duration: 0.2 }}
                        >
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Limit Count <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="number"
                            min="1"
                            step="1"
                            value={formData.limit_count}
                            onChange={(e) => {
                              setFormData({ ...formData, limit_count: e.target.value });
                              if (formErrors.limit_count) {
                                setFormErrors({ ...formErrors, limit_count: null });
                              }
                            }}
                            className={`
                              w-full px-4 py-3 bg-white dark:bg-gray-800 
                              border-2 rounded-xl text-sm text-gray-900 dark:text-white
                              focus:ring-2 focus:ring-purple-500 focus:border-transparent
                              transition-all duration-200
                              ${formErrors.limit_count 
                                ? 'border-red-300 focus:ring-red-500' 
                                : 'border-gray-200 dark:border-gray-700 hover:border-purple-300'
                              }
                            `}
                            placeholder="Enter limit count (must be > 0)"
                          />
                          {formErrors.limit_count && (
                            <motion.p
                              initial={{ opacity: 0, y: -10 }}
                              animate={{ opacity: 1, y: 0 }}
                              className="mt-2 text-xs text-red-500 flex items-center gap-1"
                            >
                              <FiAlertCircle className="w-3 h-3" />
                              {formErrors.limit_count}
                            </motion.p>
                          )}
                          <p className="mt-2 text-xs text-gray-500">
                            Maximum number of times this item can be used in custom items
                          </p>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                </div>

                <div className="border-t-2 border-gray-100 dark:border-gray-700 px-6 py-4 bg-white dark:bg-gray-800">
                  <div className="flex justify-end gap-3">
                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={() => setShowModal(false)}
                      className="px-6 py-3 rounded-xl font-medium text-sm border-2 border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-all"
                      disabled={isSubmitting}
                    >
                      Cancel
                    </motion.button>
                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={modalType === 'create' ? handleCreate : handleUpdate}
                      disabled={isSubmitting}
                      className={`px-8 py-3 bg-gradient-to-r ${theme.primary} text-white rounded-xl font-medium text-sm hover:from-purple-700 hover:to-purple-800 transition-all shadow-lg shadow-purple-500/20 flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed`}
                    >
                      {isSubmitting ? (
                        <>
                          <FiRefreshCw className="w-4 h-4 animate-spin" />
                          <span>Processing...</span>
                        </>
                      ) : (
                        <>
                          <FiSave className="w-4 h-4" />
                          <span>{modalType === 'create' ? 'Create' : 'Update'}</span>
                        </>
                      )}
                    </motion.button>
                  </div>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Delete Confirmation Modal */}
      <AnimatePresence>
        {showDeleteModal && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setShowDeleteModal(false)}
              className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50"
            />
            
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className="fixed inset-0 z-50 flex items-center justify-center p-4"
            >
              <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-md">
                <div className="p-6">
                  <div className="flex items-center gap-4 mb-4">
                    <div className="w-12 h-12 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center">
                      <FiAlertCircle className="w-6 h-6 text-red-600 dark:text-red-400" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Delete Item</h3>
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        Are you sure you want to delete this item?
                      </p>
                    </div>
                  </div>
                  
                  {itemToDelete && (
                    <div className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-4 mb-6">
                      <p className="font-medium text-gray-900 dark:text-white">{itemToDelete.name}</p>
                      <p className="text-sm text-gray-500 dark:text-gray-400">Code: {itemToDelete.code}</p>
                      {itemToDelete.has_limit === 1 && (
                        <p className="text-xs text-amber-600 dark:text-amber-400 mt-1">
                          Limit: {itemToDelete.limit_count}
                        </p>
                      )}
                    </div>
                  )}
                  
                  <div className="flex justify-end gap-3">
                    <button
                      onClick={() => setShowDeleteModal(false)}
                      className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleDelete}
                      className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors flex items-center gap-2"
                    >
                      <FiTrash2 className="w-4 h-4" />
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      <style jsx>{`
        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }
        .scrollbar-hide {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
      `}</style>
    </div>
  );
};

export default BasedProductItems;
